import React, { useState, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Box,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { getSupportDevices, searchRestaurants } from 'apis/supportAPIs';
import { SupportRoleGuard, DeviceTable } from './components';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    flexGrow: 1,
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    maxWidth: 500,
    marginBottom: theme.spacing(3),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  piiWarning: {
    marginBottom: theme.spacing(2),
  },
}));

const SupportLanding = () => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const currentUserRestaurantId = useSelector(
    (state) => state.account?.user?.restaurantId || ''
  );

  const selectedRestaurantId = selectedRestaurant?.restaurantId || selectedRestaurant?.id;

  const {
    data: devices,
    isLoading: devicesLoading,
    isError: devicesError,
    error: devicesErr,
  } = useQuery({
    queryKey: ['supportDevices', selectedRestaurantId],
    queryFn: () => getSupportDevices(selectedRestaurantId),
    enabled: !!selectedRestaurantId,
    staleTime: 30000,
  });

  const handleSearch = useCallback(
    async (e) => {
      const query = e.target.value;
      setSearchQuery(query);

      if (query.trim().length < 2) {
        setSearchResults([]);
        return;
      }

      setSearchLoading(true);
      setSearchError(null);
      try {
        const result = await searchRestaurants(query.trim());
        const restaurants = Array.isArray(result)
          ? result
          : result?.restaurants || [];
        setSearchResults(restaurants);
      } catch (err) {
        setSearchError(err.message || 'Search failed');
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    },
    []
  );

  const handleSelectRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setSearchQuery(restaurant.name || restaurant.restaurantId);
    setSearchResults([]);
  };

  const handleLoadMyRestaurant = () => {
    if (!currentUserRestaurantId) return;
    setSearchError(null);
    handleSelectRestaurant({ restaurantId: currentUserRestaurantId });
  };

  return (
    <SupportRoleGuard>
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h4" gutterBottom>
            Support Portal
          </Typography>
          <Alert severity="info" className={classes.piiWarning}>
            All actions on this portal are audit-logged. Screenshots and transaction exports may
            contain PII — do not share outside of authorised support workflows.
          </Alert>
        </div>

        <div className={classes.searchContainer}>
          {currentUserRestaurantId && (
            <Button
              variant="outlined"
              startIcon={<StorefrontIcon />}
              onClick={handleLoadMyRestaurant}
              sx={{ mb: 1.5 }}
              aria-label={`Load devices for ${currentUserRestaurantId}`}
            >
              Load my restaurant&apos;s devices ({currentUserRestaurantId})
            </Button>
          )}

          <TextField
            fullWidth
            label="Search merchant"
            placeholder="Restaurant name or ID"
            value={searchQuery}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={20} />
                </InputAdornment>
              ) : null,
            }}
            inputProps={{ 'aria-label': 'Search merchant by name or ID' }}
          />

          {searchError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {searchError}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mt: 0.5,
                maxHeight: 240,
                overflow: 'auto',
                bgcolor: 'background.paper',
              }}
              role="listbox"
              aria-label="Search results"
            >
              {searchResults.map((r) => (
                <Box
                  key={r.restaurantId || r.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                  role="option"
                  tabIndex={0}
                  onClick={() => handleSelectRestaurant(r)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectRestaurant(r);
                    }
                  }}
                  aria-label={`Select ${r.name || r.restaurantId}`}
                >
                  <Typography variant="body2" fontWeight={500}>
                    {r.name || 'Unnamed'}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {r.restaurantId || r.id}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </div>

        {selectedRestaurant && (
          <div className={classes.content}>
            <Typography variant="h6" gutterBottom>
              Devices for {selectedRestaurant.name || selectedRestaurantId}
            </Typography>

            {devicesError && (
              <Alert severity="error">{devicesErr?.message || 'Failed to load devices'}</Alert>
            )}

            <DeviceTable
              devices={devices || []}
              restaurantId={selectedRestaurantId}
              loading={devicesLoading}
            />
          </div>
        )}

        {!selectedRestaurant && !searchLoading && searchResults.length === 0 && searchQuery.length < 2 && (
          <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
            <SearchIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="body1">
              Search for a merchant to view their devices
            </Typography>
          </Box>
        )}
      </div>
    </SupportRoleGuard>
  );
};

export default SupportLanding;
